import React, {useState} from 'react'
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import WbTextField from '../../../common/WbTextField'
import WbOutlineButton from '../../../common/WbOutlineButton'
import {
  ATTRIBUTE_KEYS,
  OPERATORS,
  PARTICIPANT_TYPES,
  SET_OPERATORS,
  conditionIssues,
  conditionKind,
  emptyAnswerCondition,
  emptyAttributeCondition,
  emptyGroup,
  emptyParticipantCondition,
  operatorsForQuestionType,
  withAnswerOperator,
} from '../../../../utils/skipLogic/conditions'

const MATCH_OPTIONS = [
  {value: 'all', label: 'all'},
  {value: 'any', label: 'any'},
]

const RemoveControl = ({label, onRemove}) => (
  <Link
    component="button"
    type="button"
    color="#E84F6B"
    underline="hover"
    aria-label={label}
    onClick={onRemove}
  >
    Remove
  </Link>
)

// A native <select> of the two set operators (is any of / is none of), used by
// participant and attribute conditions.
const SetOperatorSelect = ({value, onChange, id, label}) => (
  <WbTextField
    select
    SelectProps={{native: true}}
    InputLabelProps={{shrink: true}}
    fullWidth={false}
    sx={{minWidth: 130}}
    id={id}
    label={label}
    value={value || 'isAnyOf'}
    onChange={e => onChange(e.target.value)}
  >
    {SET_OPERATORS.map(o => (
      <option key={o.name} value={o.name}>
        {o.label}
      </option>
    ))}
  </WbTextField>
)

// The value input for one answer condition, driven by its operator's kind.
function AnswerValue({answer, subject, idPrefix, onChange}) {
  const op = OPERATORS[answer.op]
  if (!op || op.kind === 'none') return null

  if (op.kind === 'number' || op.kind === 'range') {
    const setNum = (field, raw) =>
      onChange({...answer, [field]: raw === '' ? null : Number(raw)})
    if (op.kind === 'number') {
      return (
        <WbTextField
          type="number"
          fullWidth={false}
          sx={{width: 120}}
          id={`${idPrefix}-number`}
          label="value"
          value={answer.number ?? ''}
          onChange={e => setNum('number', e.target.value)}
        />
      )
    }
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <WbTextField
          type="number"
          fullWidth={false}
          sx={{width: 100}}
          id={`${idPrefix}-min`}
          label="from"
          value={answer.min ?? ''}
          onChange={e => setNum('min', e.target.value)}
        />
        <Typography variant="body2">and</Typography>
        <WbTextField
          type="number"
          fullWidth={false}
          sx={{width: 100}}
          id={`${idPrefix}-max`}
          label="to"
          value={answer.max ?? ''}
          onChange={e => setNum('max', e.target.value)}
        />
      </Stack>
    )
  }

  const options = subject?.options || []
  if (!options.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        This question has no options to choose.
      </Typography>
    )
  }
  const selected = answer.values || []
  const toggle = value => {
    const next =
      op.kind === 'one'
        ? [value]
        : selected.includes(value)
          ? selected.filter(v => v !== value)
          : [...selected, value]
    onChange({...answer, values: next})
  }
  return (
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
      {options.map(o => (
        <FormControlLabel
          key={o.value}
          control={
            <Checkbox
              size="small"
              checked={selected.includes(o.value)}
              onChange={() => toggle(o.value)}
            />
          }
          label={o.label}
        />
      ))}
    </Box>
  )
}

function AnswerCondition({condition, subjects, idPrefix, onChange, onRemove, number}) {
  const answer = condition.answer
  const subject = subjects.find(s => s.itemId === answer.item)
  const operators = operatorsForQuestionType(subject?.questionType)
  const issues = conditionIssues(condition, subjects)

  const setSubject = itemId => {
    const next = subjects.find(s => s.itemId === itemId)
    onChange(emptyAnswerCondition(itemId, operatorsForQuestionType(next?.questionType)[0]))
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{flexWrap: 'wrap'}}
      role="group"
      aria-label={`Condition ${number}`}
    >
      <WbTextField
        select
        SelectProps={{native: true}}
        InputLabelProps={{shrink: true}}
        fullWidth={false}
        sx={{minWidth: 170}}
        id={`${idPrefix}-question`}
        label={`Condition ${number} question`}
        value={answer.item ?? ''}
        onChange={e => setSubject(Number(e.target.value))}
      >
        {subjects.map(s => (
          <option key={s.itemId} value={s.itemId}>
            {s.label}
          </option>
        ))}
      </WbTextField>
      <WbTextField
        select
        SelectProps={{native: true}}
        InputLabelProps={{shrink: true}}
        fullWidth={false}
        sx={{minWidth: 150}}
        id={`${idPrefix}-operator`}
        label={`Condition ${number} operator`}
        value={answer.op || ''}
        onChange={e => onChange(withAnswerOperator(answer, e.target.value))}
      >
        {operators.map(op => (
          <option key={op} value={op}>
            {OPERATORS[op].label}
          </option>
        ))}
      </WbTextField>
      <AnswerValue
        answer={answer}
        subject={subject}
        idPrefix={idPrefix}
        onChange={next => onChange({answer: next})}
      />
      <RemoveControl label={`Remove condition ${number}`} onRemove={onRemove} />
      {issues.length > 0 && (
        <Typography
          color="error"
          variant="caption"
          role="alert"
          sx={{width: '100%'}}
        >
          {issues[0]}
        </Typography>
      )}
    </Stack>
  )
}

function ParticipantCondition({condition, idPrefix, number, onChange, onRemove}) {
  const p = condition.participant
  const values = p.values || []
  const toggle = v =>
    onChange({
      participant: {
        ...p,
        values: values.includes(v) ? values.filter(x => x !== v) : [...values, v],
      },
    })
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{flexWrap: 'wrap'}}
      role="group"
      aria-label={`Condition ${number}`}
    >
      <Typography variant="body2">The participant type</Typography>
      <SetOperatorSelect
        value={p.op}
        onChange={op => onChange({participant: {...p, op}})}
        id={`${idPrefix}-operator`}
        label={`Condition ${number} operator`}
      />
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
        {PARTICIPANT_TYPES.map(t => (
          <FormControlLabel
            key={t.value}
            control={
              <Checkbox
                size="small"
                checked={values.includes(t.value)}
                onChange={() => toggle(t.value)}
              />
            }
            label={t.label}
          />
        ))}
      </Box>
      <RemoveControl label={`Remove condition ${number}`} onRemove={onRemove} />
    </Stack>
  )
}

function AttributeCondition({condition, idPrefix, number, onChange, onRemove}) {
  const a = condition.attribute
  const spec = ATTRIBUTE_KEYS.find(k => k.key === a.key) || ATTRIBUTE_KEYS[0]
  const values = a.values || []
  const [draft, setDraft] = useState('')

  const setValues = next => onChange({attribute: {...a, values: next}})
  const addFree = () => {
    const v = draft.trim()
    if (v && !values.includes(v)) setValues([...values, v])
    setDraft('')
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{flexWrap: 'wrap'}}
      role="group"
      aria-label={`Condition ${number}`}
    >
      <WbTextField
        select
        SelectProps={{native: true}}
        InputLabelProps={{shrink: true}}
        fullWidth={false}
        sx={{minWidth: 130}}
        id={`${idPrefix}-attribute`}
        label={`Condition ${number} attribute`}
        value={a.key}
        onChange={e => onChange({attribute: {key: e.target.value, op: a.op, values: []}})}
      >
        {ATTRIBUTE_KEYS.map(k => (
          <option key={k.key} value={k.key}>
            {k.label}
          </option>
        ))}
      </WbTextField>
      <SetOperatorSelect
        value={a.op}
        onChange={op => onChange({attribute: {...a, op}})}
        id={`${idPrefix}-operator`}
        label={`Condition ${number} operator`}
      />
      {spec.free ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
          {values.map(v => (
            <Chip
              key={v}
              label={v}
              size="small"
              onDelete={() => setValues(values.filter(x => x !== v))}
            />
          ))}
          <WbTextField
            fullWidth={false}
            sx={{width: 120}}
            id={`${idPrefix}-value`}
            label="value"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addFree()
              }
            }}
          />
          <Link
            component="button"
            type="button"
            underline="hover"
            aria-label={`Add value to condition ${number}`}
            onClick={addFree}
          >
            Add
          </Link>
        </Stack>
      ) : (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
          {(spec.values || []).map(v => (
            <FormControlLabel
              key={v}
              control={
                <Checkbox
                  size="small"
                  checked={values.includes(v)}
                  onChange={() =>
                    setValues(
                      values.includes(v)
                        ? values.filter(x => x !== v)
                        : [...values, v]
                    )
                  }
                />
              }
              label={v}
            />
          ))}
        </Box>
      )}
      <RemoveControl label={`Remove condition ${number}`} onRemove={onRemove} />
    </Stack>
  )
}

// A group of conditions combined with ALL / ANY. Conditions may be answer,
// participant or attribute leaves, or - at the top level only - a single nested
// group (the engine allows one level of nesting).
function ConditionGroup({when, subjects, onChange, idPrefix, depth = 0}) {
  const mode = Array.isArray(when?.any) ? 'any' : 'all'
  const conditions = when?.[mode] || []
  const setMode = newMode => onChange({[newMode]: conditions})
  const setConditions = next => onChange({[mode]: next})
  const updateCondition = (i, cond) =>
    setConditions(conditions.map((c, idx) => (idx === i ? cond : c)))
  const removeCondition = i =>
    setConditions(conditions.filter((_, idx) => idx !== i))

  const addAnswer = () => {
    const first = subjects[0]
    if (!first) return
    const op = operatorsForQuestionType(first.questionType)[0]
    setConditions([...conditions, emptyAnswerCondition(first.itemId, op)])
  }
  const addParticipant = () =>
    setConditions([...conditions, emptyParticipantCondition()])
  const addAttribute = () =>
    setConditions([...conditions, emptyAttributeCondition()])
  const addGroup = () => setConditions([...conditions, emptyGroup('all')])

  return (
    <Box
      sx={{
        mb: 1,
        ...(depth > 0 ? {pl: 2, borderLeft: '2px solid #D7E3E8'} : {}),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1, flexWrap: 'wrap'}}>
        <Typography variant="body2">When the respondent matches</Typography>
        <WbTextField
          select
          SelectProps={{native: true}}
          InputLabelProps={{shrink: true}}
          fullWidth={false}
          sx={{width: 90}}
          id={`${idPrefix}-match`}
          label={depth > 0 ? 'Group match' : 'Match'}
          value={mode}
          onChange={e => setMode(e.target.value)}
        >
          {MATCH_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </WbTextField>
        <Typography variant="body2">of these:</Typography>
      </Stack>

      {conditions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No conditions yet - this {depth > 0 ? 'group' : 'rule'} applies to
          everyone. Add one to narrow it.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {conditions.map((c, i) => {
            const kind = conditionKind(c)
            const shared = {
              number: i + 1,
              condition: c,
              idPrefix: `${idPrefix}-${i}`,
              onChange: cond => updateCondition(i, cond),
              onRemove: () => removeCondition(i),
            }
            if (kind === 'participant') return <ParticipantCondition key={i} {...shared} />
            if (kind === 'attribute') return <AttributeCondition key={i} {...shared} />
            if (kind === 'group') {
              return (
                <Box key={i} role="group" aria-label={`Group ${i + 1}`}>
                  <ConditionGroup
                    when={c}
                    subjects={subjects}
                    idPrefix={`${idPrefix}-${i}`}
                    depth={depth + 1}
                    onChange={cond => updateCondition(i, cond)}
                  />
                  <RemoveControl
                    label={`Remove group ${i + 1}`}
                    onRemove={() => removeCondition(i)}
                  />
                </Box>
              )
            }
            return <AnswerCondition key={i} subjects={subjects} {...shared} />
          })}
        </Stack>
      )}

      {subjects.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
          Add questions before this section to test earlier answers. Participant
          and attribute conditions are available below.
        </Typography>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{mt: 1, flexWrap: 'wrap'}}>
        {subjects.length > 0 && (
          <WbOutlineButton CustomOutlineButtonText="Add condition" onClick={addAnswer} />
        )}
        <Link component="button" type="button" underline="hover" onClick={addParticipant}>
          Add participant condition
        </Link>
        <Link component="button" type="button" underline="hover" onClick={addAttribute}>
          Add attribute condition
        </Link>
        {depth === 0 && (
          <Link component="button" type="button" underline="hover" onClick={addGroup}>
            Add group
          </Link>
        )}
      </Stack>
    </Box>
  )
}

// The "if" side of a rule: the top-level condition group.
export default function SkipLogicConditions({
  when,
  subjects = [],
  onChange,
  idPrefix = 'cond',
}) {
  return (
    <ConditionGroup
      when={when}
      subjects={subjects}
      onChange={onChange}
      idPrefix={idPrefix}
      depth={0}
    />
  )
}
